// @djsem/ui — React hooks that bind the DJSEMApiClient to screens (fix report Phase 4).
//
// useApiResource loads a resource and exposes the canonical UI state + a refetch. useApiAction
// runs a mutation and exposes pending/result. These keep every screen's data-loading identical
// and route errors through the canonical UI-state mapper.

import { useCallback, useEffect, useMemo, useState } from "react";
import { DJSEMApiClient } from "./apiClient";
import type { DJSEMApiResult } from "./apiClient";
import { loadingResource, mapResultToUiState, type AsyncResource, type UiStateView } from "./apiState";

/**
 * Create a memoised {@link DJSEMApiClient} bound to a base URL (and optional fetch impl for tests).
 * @param baseUrl - Absolute base URL of the DJSEM worker API.
 * @param fetchImpl - Injected fetch implementation (tests only); defaults to global fetch.
 * @returns A client instance, memoised across renders while `baseUrl`/`fetchImpl` are stable.
 */
export function useApiClient(baseUrl: string, fetchImpl?: typeof fetch): DJSEMApiClient {
  return useMemo(() => new DJSEMApiClient({ baseUrl, fetchImpl }), [baseUrl, fetchImpl]);
}

/** Return value of {@link useApiResource}: the canonical async UI state plus a manual refetch. */
export interface UseApiResource<T> extends AsyncResource<T> {
  refetch: () => void;
}

/**
 * Load a resource on mount (and when deps change); expose UI state + refetch.
 * @param fetcher - Called to (re)load the resource; must resolve to a {@link DJSEMApiResult}.
 * @param deps - Dependency array; changing it re-runs `fetcher` (in addition to `refetch()`).
 * @param isEmpty - Optional predicate distinguishing ready-but-empty data from ready-with-data.
 * @returns The current {@link UseApiResource}, including a `refetch` to force a reload.
 */
export function useApiResource<T>(
  fetcher: () => Promise<DJSEMApiResult<T>>,
  deps: unknown[] = [],
  isEmpty?: (data: T) => boolean,
): UseApiResource<T> {
  const [resource, setResource] = useState<AsyncResource<T>>(loadingResource<T>());
  const [nonce, setNonce] = useState(0);
  const run = useCallback(async () => {
    setResource(loadingResource<T>());
    const result = await fetcher();
    setResource({ view: mapResultToUiState(result, isEmpty), loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);
  useEffect(() => { void run(); }, [run]);
  return { ...resource, refetch: () => setNonce((n) => n + 1) };
}

/** Return value of {@link useApiAction}: a runner plus its pending flag and mapped result state. */
export interface UseApiAction<A extends unknown[], T> {
  run: (...args: A) => Promise<void>;
  pending: boolean;
  view?: UiStateView<T>;
}

/**
 * Wrap a mutation so a screen gets pending + the mapped result state.
 * @param action - The mutation to run; must resolve to a {@link DJSEMApiResult}.
 * @returns A {@link UseApiAction} exposing `run`, `pending`, and the mapped `view` after it settles.
 */
export function useApiAction<A extends unknown[], T>(
  action: (...args: A) => Promise<DJSEMApiResult<T>>,
): UseApiAction<A, T> {
  const [pending, setPending] = useState(false);
  const [view, setView] = useState<UiStateView<T> | undefined>(undefined);
  const run = useCallback(async (...args: A) => {
    setPending(true);
    const result = await action(...args);
    setView(mapResultToUiState(result));
    setPending(false);
  }, [action]);
  return { run, pending, view };
}
